<script setup>
/**
 * The one file picker in the Waslo tree. THE PLAN, §5 R7.
 *
 * WHY IT IS THE THIRD FILE IN R7'S EXEMPTION LIST, and why the list did not just get
 * looser. R7 says user text goes through a `dir="auto"` element, and it enforces that by
 * banning raw `<input>` everywhere except the two wrappers that have to write one. A file
 * picker cannot be `WasloInput`: `<input type="file">` refuses any programmatic `value`
 * that is not the empty string (`InvalidStateError`), which is exactly what a v-model
 * wrapper does on every render, and its model is a `File`, not a string. So somebody has
 * to write a third raw element — and the honest thing is to name it in the exemption
 * list, where it stays auditable, rather than to reach it through
 * `<component :is="'input'">`, which would pass the check while quietly making the check
 * unenforceable everywhere.
 *
 * The rule's PURPOSE is honoured here even though its letter does not apply: the one
 * piece of text this control renders is the chosen file's name, which on a Kuwaiti
 * owner's laptop is as likely to be `قائمة الأسعار.pdf` as `prices.pdf`. It is rendered
 * inside a `<bdi>` isolate, so its extension cannot be dragged to the wrong end of the
 * line by the neutral characters around it — the same treatment `AttentionRow` gives a
 * channel name, for the same reason.
 *
 * The picker is opened by a `<label for>`, not by a click handler on a hidden input.
 * That is one less thing to get wrong: labels open file pickers natively, keyboard and
 * pointer alike, and a real focus ring lands on a real focusable element. The input is
 * `sr-only` rather than `hidden`, because `display: none` takes it out of the tab order
 * and the control becomes keyboard-unreachable.
 */
import { computed, getCurrentInstance, ref } from 'vue';

const props = defineProps({
  /**
   * `File|null`, or with `multiple` (P-6.7's photos) an array of `File`, empty for none.
   * @type {import('vue').PropType<File|File[]|null>}
   */
  modelValue: { type: [Object, Array], default: null },
  /** P-6.7: choose several files at once; `modelValue` is then an array. */
  multiple: { type: Boolean, default: false },
  label: { type: String, default: '' },
  /** What to write in the box, above the box. See `WasloTextArea`. */
  hint: { type: String, default: '' },
  /** The label on the button — changes once a file has been chosen. */
  chooseLabel: { type: String, default: '' },
  changeLabel: { type: String, default: '' },
  /** What stands in for the filename before anything is chosen. */
  emptyLabel: { type: String, default: '' },
  /** Hint to the OS picker only. The BFF re-checks the type AND the bytes. */
  accept: { type: String, default: '' },
  message: { type: String, default: '' },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const fieldId = `waslo-file-${getCurrentInstance().uid}`;
const fieldRef = ref(null);

const messageClass = computed(() =>
  props.messageType === 'error' ? 'text-n-ruby-9' : 'text-n-slate-11'
);

// Same resting edge as every other control in this section: `n-weak` is a divider token
// and made unfocused fields read as printed values rather than things you can change.
const borderClass = computed(() => {
  if (props.disabled) return 'border-n-slate-5 opacity-50 cursor-not-allowed';
  if (props.messageType === 'error' && props.message) {
    return 'border-n-ruby-8 hover:border-n-ruby-9';
  }
  return 'border-n-slate-7 hover:border-n-slate-8';
});

const chosen = computed(() =>
  Array.isArray(props.modelValue)
    ? props.modelValue
    : [props.modelValue].filter(Boolean)
);
const hasFile = computed(() => chosen.value.length > 0);
const fileName = computed(() => chosen.value.map(file => file.name).join(', '));

const onChange = event => {
  const files = Array.from(event.target.files ?? []);
  if (props.multiple) {
    emit('update:modelValue', files);
    return;
  }
  emit('update:modelValue', files[0] ?? null);
};

/**
 * Choosing the same file twice in a row fires no `change` event, because the input's
 * value has not changed — so an owner who picked the wrong file, cleared the form and
 * picked the right one from the same dialog would silently submit nothing. Clearing the
 * element on every open makes the next pick always a change.
 */
const onClick = event => {
  event.target.value = '';
};

defineExpose({ focus: () => fieldRef.value?.focus() });
</script>

<template>
  <div class="flex flex-col min-w-0">
    <label
      v-if="label"
      :for="fieldId"
      class="mb-1 text-sm font-medium text-n-slate-12"
    >
      {{ label }}
    </label>
    <p v-if="hint" class="min-w-0 mb-2 text-sm text-n-slate-11">{{ hint }}</p>

    <div
      class="flex items-center gap-3 p-2 transition-all duration-300 ease-in-out border rounded-lg bg-n-alpha-black2 focus-within:has-[:focus-visible]:ring-2 focus-within:has-[:focus-visible]:ring-n-strong"
      :class="borderClass"
    >
      <label
        :for="fieldId"
        class="inline-flex items-center h-8 px-3 text-sm font-medium rounded-lg cursor-pointer shrink-0 text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4"
      >
        {{ hasFile ? changeLabel : chooseLabel }}
      </label>

      <!-- The third and last sanctioned raw input. `waslo-lint` exempts this file by
           name; see the header and R7. -->
      <input
        :id="fieldId"
        ref="fieldRef"
        type="file"
        :accept="accept || undefined"
        :multiple="multiple"
        :disabled="disabled"
        class="sr-only"
        @click="onClick"
        @change="onChange"
      />

      <p
        class="min-w-0 mb-0 text-sm truncate"
        :class="hasFile ? 'text-n-slate-12' : 'text-n-slate-10'"
        data-waslo-file-name
      >
        <!-- An isolate, not just `dir="auto"`: `قائمة الأسعار.pdf` inside an LTR line
             loses its own extension to the surrounding neutrals otherwise. -->
        <bdi>{{ fileName || emptyLabel }}</bdi>
      </p>
    </div>

    <p v-if="message" class="min-w-0 mt-2 mb-0 text-sm" :class="messageClass">
      {{ message }}
    </p>
  </div>
</template>
